import {Text} from '@mantine/core';
import React from 'react';

import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';

interface UpdateRunDesignConfirmationModalProps {
  conditionReplicateCount: number;
  removedConditionReplicateCount: number;
  activeMethodName: string | null;
  downstreamUnitOperationCount: number | null;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

function getConditionReplicateText(conditionReplicateCount: number): string {
  return `${conditionReplicateCount} ${pluralize(conditionReplicateCount, 'condition replicate')}`;
}

function getConditionReplicateChangeText(
  addedConditionReplicateCount: number,
  removedConditionReplicateCount: number
): React.ReactNode {
  if (addedConditionReplicateCount === 0 && removedConditionReplicateCount > 0) {
    return (
      <Text span={true} fw="bold">
        Removing {getConditionReplicateText(removedConditionReplicateCount)}
      </Text>
    );
  }
  const addingText = `Adding ${getConditionReplicateText(addedConditionReplicateCount)}`;
  if (removedConditionReplicateCount > 0) {
    return (
      <>
        <Text span={true} fw="bold">
          {addingText}
        </Text>{' '}
        and{' '}
        <Text span={true} fw="bold">
          removing {getConditionReplicateText(removedConditionReplicateCount)}
        </Text>
      </>
    );
  }
  return (
    <Text span={true} fw="bold">
      {addingText}
    </Text>
  );
}

function renderConditionReplicateChangeText({
  changeText,
  activeMethodName,
  downstreamUnitOperationCount,
}: {
  changeText: React.ReactNode;
  activeMethodName: string | null;
  downstreamUnitOperationCount: number | null;
}): JSX.Element {
  // We only allow editing the run design with a null active method when split-pool is disabled.
  // In this case, the change applies to all methods and we don't need to specify which ones.
  if (activeMethodName == null) {
    return <Text size="md">{changeText}</Text>;
  }

  assert(
    downstreamUnitOperationCount != null,
    'Downstream unit operation count should not be null when active method is not null'
  );
  const downstreamUnitOperationText = `${downstreamUnitOperationCount} ${pluralize(
    downstreamUnitOperationCount,
    'downstream unit operation'
  )}`;
  const shouldIncludeDownstreamUnitOperations = downstreamUnitOperationCount > 0;
  return (
    <Text size="md">
      {changeText} at{' '}
      <Text span={true} fw="bold">
        {activeMethodName}
      </Text>
      {shouldIncludeDownstreamUnitOperations && <> and {downstreamUnitOperationText}</>}
    </Text>
  );
}

export default function UpdateRunDesignConfirmationModal({
  conditionReplicateCount,
  removedConditionReplicateCount,
  activeMethodName,
  downstreamUnitOperationCount,
  onRequestClose,
  onRequestSubmit,
}: UpdateRunDesignConfirmationModalProps): JSX.Element {
  return (
    <Modal
      dataTestAttrs={{component: 'UpdateRunDesignConfirmationModal', element: 'root'}}
      title="Update run design?"
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
          style: ButtonStyle.TERTIARY_GRAY,
        },
        {
          text: 'Update',
          submit: true,
          style: ButtonStyle.PRIMARY,
          dataTestAttrs: {component: 'UpdateRunDesignConfirmationModal', element: 'updateButton'},
        },
      ]}
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
    >
      {renderConditionReplicateChangeText({
        changeText: getConditionReplicateChangeText(conditionReplicateCount, removedConditionReplicateCount),
        activeMethodName,
        downstreamUnitOperationCount,
      })}
    </Modal>
  );
}
